import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

import {
  colorGreyShark,
  colorGreyRaven,
  colorGreyGainsboro,
  colorTelusPurple,
} from "@tds/core-colours";

const minWidth = "768px";

const Wrapper = styled.div`
  // Reset.
  h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6, p, a, em, i, img, strong, b, ol, ul, li, span, sub, sup {
    margin: 0;
    padding: 0;
    border: 0;
    color: ${colorGreyShark};
    font: inherit;
    font-size: 1rem;
    font-weight: 400;
    vertical-align: baseline;
    text-rendering: optimizeLegibility;
    letter-spacing: -0.8px;
    line-height: 1.5;
  }

  // Alignment.
  .align-left {
    float: left;
  }

  .align-right {
    float: right;
  }

  .align-center {
    display: block;
    margin-right: auto;
    margin-left: auto;
  }

  .text-align-left {
    text-align: left;
  }

  .text-align-right {
    text-align: right;
  }

  .text-align-center {
    text-align: center;
  }

  // Headings.
  h1, .h1 {
    margin-bottom: 0.5rem;
    font-size: 1.75rem;
    font-weight: 400;
    color: ${colorTelusPurple};
    line-height: 1.29;
    letter-spacing: -1.6px;
    overflow-wrap: break-word;

    @media (min-width: ${minWidth}) {
      font-size: 2.75rem;
      font-weight: 300;
      line-height: 1.18;
      letter-spacing: 0;
    }
  }

  h2, .h2 {
    margin-bottom: 0.5rem;
    font-size: 1.5rem;
    font-weight: 400;
    color: ${colorTelusPurple};
    line-height: 1.33;
    letter-spacing: -0.7px;
    overflow-wrap: break-word;

    @media (min-width: ${minWidth}) {
      font-size: 1.75rem;
      line-height: 1.29;
      letter-spacing: -0.8px;
    }
  }

  h3, .h3 {
    margin-bottom: 0.5rem;
    font-size: 1.25rem;
    font-weight: 700;
    line-height: 1.4;
    letter-spacing: -0.6px;
    overflow-wrap: break-word;
  }

  h4, .h4, h5, .h5, h6, .h6 {
    margin-bottom: 0.5rem;
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.25;
    letter-spacing: -0.6px;
    overflow-wrap: break-word;
  }

  // Additional elements.
  sub {
    vertical-align: sub;
    font-size: 0.7rem; 
  }

  sup {
    vertical-align: super;
    font-size: 0.7rem; 
  }

  p {
    margin-bottom: 1rem;
  }

  ul {
    display: flex;
    flex-direction: column;
    list-style: none;
    padding-left: 3rem;
    margin-bottom: 0.5rem;

    li {
      position: relative;
      margin-bottom: 0.5rem;
      overflow-wrap: break-word;

      &:before {
        display: block;
        position: absolute;
        top: 0.65rem;
        left: -2rem;
        width: 0.32rem;
        height: 0.32rem;
        content: "";
        line-height: 1.6;
        color: ${colorTelusPurple};
        background-image: url(data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMTAwIDEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4gIDxjaXJjbGUgY3g9IjUwIiBjeT0iNTAiIHI9IjUwIiBmaWxsPSIjNGIyODZkIiAvPjwvc3ZnPg==);
        background-repeat: no-repeat;
        background-size: 100% 100%;
        box-sizing: border-box;
      },
    }
  }

  ol {
    display: flex;
    flex-direction: column;
    list-style: none;
    list-style-type: decimal;
    padding-left: 3rem;
    margin-bottom: 0.5rem;

    li {
      margin-bottom: 0.5rem;
      margin-left: -1rem;
      padding-left: 1rem;
      overflow-wrap: break-word;
    }
  }

  a {
    color: ${colorGreyShark};
    text-decoration: underline;
    background-color: transparent;

    &:hover {
      color: colorGreyShark;
      text-decoration: none;
    }
    &:active {
      padding: 0.125rem;
      margin: -0.125rem;
      color: ${colorGreyShark};
      text-decoration: underline;
      background-color: ${colorGreyGainsboro};
      border-radius: 0.25rem;
    }
    &:focus {
      padding: 0.125rem;
      margin: -0.25rem;
      color: ${colorGreyShark};
      text-decoration: underline;
      border: 0.125rem solid ${colorGreyRaven};
      border-radius: 0.25rem;
      outline: none;
    }
  }

  img {
    display: inline-block;
    margin: 0;
    padding: 0;
    width: 100%;
    height: auto;
    maxWidth: 100%;
  }

  strong, b {
    font-weight: 700;
    overflow-wrap: break-word;
  }

  em {
    font-style: italic;
  }

  // Custom classes.
  .small {
    font-size: 0.875rem;
    font-weight: 500;
    letter-spacing: -0.6px;
    line-height: 1.42857;
    overflow-wrap: break-word;
  }
`;

const NativeHTML = (props) => {
  const { content, className } = props;

  return (
    <Wrapper>
      <div
        className={className}
        dangerouslySetInnerHTML={{ __html: content }} // eslint-disable-line
      />
    </Wrapper>
  );
};

NativeHTML.propTypes = {
  content: PropTypes.string,
  className: PropTypes.string,
};

NativeHTML.defaultProps = {
  content: "",
  className: "",
};

export default NativeHTML;
